import { DefaultTreeAdapterTypes, parseFragment, serialize } from 'parse5';

export interface SnippetScript {
  /** Every attribute the editor wrote, `src`, `type`, `data-usercentrics`, … */
  attrs: Record<string, string>;
  /** Inline body; empty when the script has a `src`. */
  code: string;
}

export interface SplitSnippet {
  /** The fragment re-serialised, scripts removed. */
  markup: string;
  /** In document order. */
  scripts: SnippetScript[];
}

/**
 * `{query.*}` tokens, which this widget deliberately leaves unresolved.
 *
 * Every other placeholder namespace resolves from content the CMS owns.
 * `{query.*}` resolves from the request, so its value is the visitor's, and
 * the widget is the first place where a visitor's value could reach
 * executable JavaScript. `queryEntity`'s allowlist was built for a place name
 * in a heading: it blocks `<`, `"` and `=`, so a value cannot open a tag or an
 * attribute, but it admits letters, digits, spaces, `'`, `-`, `(` and `)`.
 * That is enough to break out of a single-quoted JS string. An editor writing
 * `var c = '{query.city}'` and a visitor opening `?city=x' - alert(1) - 'y`
 * would be all it takes.
 *
 * The token is left as written, which is what an unresolved placeholder does
 * everywhere else. Resolving it safely would need per-context escaping the
 * substitution layer does not have.
 */
const QUERY_TOKEN_RE = /\{query\.[^}\s]*\}/g;

/**
 * Substitutes placeholders in a snippet, `{query.*}` excepted.
 *
 * Runs before the split so a placeholder value can never close or open a tag
 * and change where the script boundaries fall.
 *
 * Substitutes the pieces between the query tokens and joins the originals back
 * in, rather than masking them with a sentinel: nothing an editor could type
 * then collides with the shield. It also means a `{query.*}` that a *resolved*
 * placeholder introduces stays literal, because each piece is substituted
 * once.
 */
export const substituteSnippet = (
  html: string,
  substitute: (value: string) => string,
): string => {
  const tokens = html.match(QUERY_TOKEN_RE) ?? [];
  return html
    .split(QUERY_TOKEN_RE)
    .map(substitute)
    .reduce((joined, piece, index) => joined + tokens[index - 1] + piece);
};

type ChildNode = DefaultTreeAdapterTypes.ChildNode;
type ParentNode = DefaultTreeAdapterTypes.ParentNode;

const hasChildNodes = (node: ChildNode): node is ChildNode & ParentNode =>
  'childNodes' in node;

const isText = (node: ChildNode): node is DefaultTreeAdapterTypes.TextNode =>
  node.nodeName === '#text';

/**
 * Splits an editor's HTML snippet into markup and scripts.
 *
 * The two halves reach the page by different routes: the markup is rendered
 * into the document, the scripts are created as elements after mount. Leaving
 * a `<script>` inside the markup would make it run on a direct page load,
 * where the browser parses it out of the server-rendered HTML, and stay inert
 * after an internal link, where React writes the same string through
 * `innerHTML`. Re-running the scripts to cover the second case would run them
 * twice in the first.
 *
 * Nothing is filtered. parse5 re-serialises what it parsed, so non-standard
 * attributes survive — the Usercentrics embed is a `<div uc-data="all" …>` and
 * an allowlist would leave an empty `<div>` that looks right and shows
 * nothing. What the round-trip does change is structure: an unclosed tag is
 * closed, so a mistake stays inside the widget instead of swallowing the rest
 * of the page.
 *
 * parse5 rather than the browser's own parser because this runs on the server
 * too, and rather than `sanitize-html` because that filters where this needs a
 * faithful round-trip.
 */
export const splitHtmlSnippet = (html: string): SplitSnippet => {
  const fragment = parseFragment(html);
  const scripts: SnippetScript[] = [];

  const visit = (parent: ParentNode) => {
    // Copy first: the walk removes from the live array it iterates.
    for (const child of [...parent.childNodes]) {
      if (child.nodeName === 'script') {
        const code = child.childNodes.find(isText)?.value ?? '';
        scripts.push({
          attrs: Object.fromEntries(
            child.attrs.map(({ name, value }) => [name, value]),
          ),
          code,
        });
        parent.childNodes.splice(parent.childNodes.indexOf(child), 1);
        continue;
      }
      if (hasChildNodes(child)) {
        visit(child);
      }
    }
  };

  visit(fragment);

  return { markup: serialize(fragment), scripts };
};
